import React from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const ContactInformationSection = ({ member, isEditing, formData, onFormChange, errors }) => {
  const addEmail = () => {
    const newEmails = [...formData?.emails, { email: '', isPrimary: false }];
    onFormChange('emails', newEmails);
  };

  const removeEmail = (index) => {
    const newEmails = formData?.emails?.filter((_, i) => i !== index);
    onFormChange('emails', newEmails);
  };

  const updateEmail = (index, field, value) => {
    const newEmails = [...formData?.emails];
    newEmails[index][field] = value;
    
    // If setting as primary, unset others
    if (field === 'isPrimary' && value) {
      newEmails?.forEach((email, i) => {
        if (i !== index) email.isPrimary = false;
      });
    }
    
    onFormChange('emails', newEmails);
  };

  const addPhone = () => {
    const newPhones = [...formData?.phones, { phone: '', type: 'mobile', isPrimary: false }];
    onFormChange('phones', newPhones);
  };

  const removePhone = (index) => {
    const newPhones = formData?.phones?.filter((_, i) => i !== index);
    onFormChange('phones', newPhones);
  };

  const updatePhone = (index, field, value) => {
    const newPhones = [...formData?.phones];
    newPhones[index][field] = value;
    
    // If setting as primary, unset others
    if (field === 'isPrimary' && value) {
      newPhones?.forEach((phone, i) => {
        if (i !== index) phone.isPrimary = false;
      });
    }
    
    onFormChange('phones', newPhones);
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 mb-6">
      <div className="flex items-center space-x-2 mb-4">
        <Icon name="Mail" size={20} className="text-primary" />
        <h2 className="text-lg font-semibold text-foreground">Contatos</h2>
      </div>
      <div className="space-y-6">
        {/* Email Addresses */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-md font-medium text-foreground">E-mails</h3>
            {isEditing && (
              <Button
                variant="outline"
                size="sm"
                onClick={addEmail}
                iconName="Plus"
                iconPosition="left"
              >
                Adicionar e-mail
              </Button>
            )}
          </div>

          <div className="space-y-3">
            {(isEditing ? formData?.emails : member?.emails)?.map((emailObj, index) => (
              <div key={index} className="flex items-center space-x-3 p-3 bg-muted rounded-lg">
                <div className="flex-grow">
                  {isEditing ? (
                    <div className="flex items-center space-x-3">
                      <Input
                        type="email"
                        value={emailObj?.email}
                        onChange={(e) => updateEmail(index, 'email', e?.target?.value)}
                        placeholder="Digite o e-mail"
                        error={errors?.emails && errors?.emails?.[index]}
                        className="flex-grow"
                      />
                      <label className="flex items-center space-x-2 text-sm">
                        <input
                          type="checkbox"
                          checked={emailObj?.isPrimary}
                          onChange={(e) => updateEmail(index, 'isPrimary', e?.target?.checked)}
                          className="rounded border-border"
                        />
                        <span>Principal</span>
                      </label>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <Icon name="Mail" size={16} className="text-muted-foreground" />
                      <span className="text-foreground">{emailObj?.email}</span>
                      {emailObj?.isPrimary && (
                        <span className="px-2 py-1 text-xs bg-primary text-primary-foreground rounded">
                          Principal
                        </span>
                      )}
                    </div>
                  )}
                </div>
                {isEditing && formData?.emails?.length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeEmail(index)}
                    iconName="Trash2"
                    className="text-error hover:text-error"
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Phone Numbers */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-md font-medium text-foreground">Telefones</h3>
            {isEditing && (
              <Button
                variant="outline"
                size="sm"
                onClick={addPhone}
                iconName="Plus"
                iconPosition="left"
              >
                Adicionar telefone
              </Button>
            )}
          </div>

          <div className="space-y-3">
            {(isEditing ? formData?.phones : member?.phones)?.map((phoneObj, index) => (
              <div key={index} className="flex items-center space-x-3 p-3 bg-muted rounded-lg">
                <div className="flex-grow">
                  {isEditing ? (
                    <div className="flex items-center space-x-3">
                      <Input
                        type="tel"
                        value={phoneObj?.phone}
                        onChange={(e) => updatePhone(index, 'phone', e?.target?.value)}
                        placeholder="Digite o telefone"
                        error={errors?.phones && errors?.phones?.[index]}
                        className="flex-grow"
                      />
                      <select
                        value={phoneObj?.type}
                        onChange={(e) => updatePhone(index, 'type', e?.target?.value)}
                        className="px-3 py-2 border border-border rounded-md bg-input text-foreground"
                      >
                        <option value="mobile">Celular</option>
                        <option value="home">Residencial</option>
                        <option value="work">Comercial</option>
                      </select>
                      <label className="flex items-center space-x-2 text-sm">
                        <input
                          type="checkbox"
                          checked={phoneObj?.isPrimary}
                          onChange={(e) => updatePhone(index, 'isPrimary', e?.target?.checked)}
                          className="rounded border-border"
                        />
                        <span>Principal</span>
                      </label>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <Icon name="Phone" size={16} className="text-muted-foreground" />
                      <span className="text-foreground">{phoneObj?.phone}</span>
                      <span className="px-2 py-1 text-xs bg-muted text-muted-foreground rounded capitalize">
                        {phoneObj?.type}
                      </span>
                      {phoneObj?.isPrimary && (
                        <span className="px-2 py-1 text-xs bg-primary text-primary-foreground rounded">
                          Principal
                        </span>
                      )}
                    </div>
                  )}
                </div>
                {isEditing && formData?.phones?.length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removePhone(index)}
                    iconName="Trash2"
                    className="text-error hover:text-error"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactInformationSection;